import React, { ReactNode } from 'react';
import Tooltip2 from 'components/Common/Tooltip2';
import Icon from 'components/Utils/NewIcon';
import { StatusHistoryEntry } from 'services/AppointmentUtils';
import './style.scss';

type Props = {
  entries: StatusHistoryEntry[];
  width?: string;
  placement?: 'right' | 'bottom';
  // Custom hover trigger. Defaults to a circle-info icon when omitted.
  children?: ReactNode;
};

/**
 * The tooltip body on its own, so it can be dropped into any tooltip mechanism
 * (Tooltip2 here, MUI Tooltip in the schedule grid where a fixed-position
 * wrapper would mis-place it). Wrap in .careand-tooltip for the boxed styling.
 */
export const AppointmentStatusHistoryContent = ({ entries }: { entries: StatusHistoryEntry[] }) => (
  <div className="appointment-status-history">
    {entries.map((entry, index) => (
      <div className="ash-entry" key={index}>
        <div className="ash-entry-head">
          <span className="ash-who">{entry.byWhom}</span>
          <span className="ash-date">{entry.date}</span>
        </div>
        {entry.changes.length > 0 && (
          <div className="ash-changes">
            {entry.changes.map((change, i) => (
              <span className="ash-change" key={i}>
                <span className="ash-change-label">{change.label}:</span>
                <span className="ash-change-value">{change.value}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    ))}
  </div>
);

/**
 * Hoverable trigger that renders the appointment's change history as a readable
 * timeline (who changed it, when, and which fields changed) inside a
 * careand-tooltip, instead of the legacy newline-joined string blob. Defaults
 * to a circle-info icon; pass children to use a different trigger.
 */
const AppointmentStatusHistory = ({
  entries,
  width = '20px',
  placement = 'bottom',
  children,
}: Props) => {
  if (!entries?.length) return null;

  return (
    <Tooltip2 placement={placement} tooltip={<AppointmentStatusHistoryContent entries={entries} />}>
      {(register) => (
        <span className="ash-trigger" {...register}>
          {children ?? <Icon src="circle-info" width={width} />}
        </span>
      )}
    </Tooltip2>
  );
};

export default AppointmentStatusHistory;
